/**
 * Generic modal of partials/modal.blade.php — behaviour of resources/js/Modal/Modal.vue.
 *
 * Modal.vue renders only while open (v-if) and teleports to <body>: the partial sits in a
 * <template>, openModal() appends a copy of it to <body>, closeModal() removes it.
 * The overlay and every [data-modal-close] element close it; Escape does not (as in Vue).
 * The page scroll is locked while it is open (shared/scroll-lock, Modal.vue's useScrollLock).
 * Same order as Modal.vue: its scroll-lock watcher runs before the render (pre flush), so
 * the page is locked before the modal is appended, and unlocked before it is removed.
 */

import { lockScroll, unlockScroll } from "@/shared/scroll-lock";

const onCloseCallbacks = new WeakMap<HTMLElement, () => void>();

export function closeModal(modal: HTMLElement): void {
    if (!modal.isConnected) return;
    unlockScroll();
    modal.remove();
    onCloseCallbacks.get(modal)?.();
}

export function openModal(template: HTMLTemplateElement, onClose?: () => void): HTMLElement {
    const fragment = template.content.cloneNode(true) as DocumentFragment;
    const modal = fragment.querySelector<HTMLElement>(".modal");
    if (!modal) {
        throw new Error("partials/modal: no .modal in the template");
    }
    if (onClose) onCloseCallbacks.set(modal, onClose);
    modal.addEventListener("click", (event) => {
        const target = event.target as HTMLElement | null;
        if (target?.closest("[data-modal-close]")) {
            closeModal(modal);
        }
    });
    lockScroll();
    document.body.appendChild(modal);
    return modal;
}
